import { jsx as _jsx } from "react/jsx-runtime";
import { authConfigSchema } from '@britania/teams-kit-core';
import { Button } from '@fluentui/react-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useMemo, useState, } from 'react';
import { createApiClient, publicApi, } from '#src/api/http-client.js';
import { DevSignIn } from '#src/auth/DevSignIn.js';
import { encodeDevToken, readDevUser, saveDevUser, } from '#src/auth/dev-identity.js';
import { StatusScreen } from '#src/shared/StatusScreen.js';
import { useTeamsHost } from '#src/teams/TeamsHostProvider.js';
const AuthContext = createContext(null);
function TeamsAuth({ getAuthToken, children, }) {
    const value = useMemo(() => ({ api: createApiClient(getAuthToken), source: 'teams' }), [getAuthToken]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
function DevAuth({ children }) {
    const queryClient = useQueryClient();
    const [user, setUser] = useState(readDevUser);
    const value = useMemo(() => {
        if (user === null) {
            return null;
        }
        const token = encodeDevToken(user);
        return {
            api: createApiClient(() => Promise.resolve(token)),
            source: 'dev',
            signOut() {
                saveDevUser(null);
                // Nada do usuário anterior pode aparecer para o próximo.
                queryClient.clear();
                setUser(null);
            },
        };
    }, [user, queryClient]);
    if (value === null) {
        return (_jsx(DevSignIn, { onSignIn: (signedIn) => {
                saveDevUser(signedIn);
                setUser(signedIn);
            } }));
    }
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
/** Fora do Teams, o modo de autenticação do backend decide o que oferecer. */
function BrowserAuth({ children }) {
    const config = useQuery({
        queryKey: ['auth-config'],
        queryFn: () => publicApi.get('/auth/config', authConfigSchema),
        staleTime: Number.POSITIVE_INFINITY,
    });
    if (config.isPending) {
        return _jsx(StatusScreen, { loading: true, title: "Carregando..." });
    }
    if (config.isError) {
        return (_jsx(StatusScreen, { tone: "error", title: "N\u00E3o foi poss\u00EDvel falar com o servidor", description: config.error.message, action: _jsx(Button, { onClick: () => config.refetch(), children: "Tentar de novo" }) }));
    }
    if (config.data.mode === 'dev') {
        return _jsx(DevAuth, { children: children });
    }
    return (_jsx(StatusScreen, { title: "Abra pelo Microsoft Teams", description: "Esta aplica\u00E7\u00E3o usa a sua conta do Teams para entrar. Procure-a na barra lateral do Teams." }));
}
export function AuthProvider({ children }) {
    const host = useTeamsHost();
    if (host !== null) {
        return _jsx(TeamsAuth, { getAuthToken: host.getAuthToken, children: children });
    }
    return _jsx(BrowserAuth, { children: children });
}
export function useAuth() {
    const value = useContext(AuthContext);
    if (value === null) {
        throw new Error('useAuth fora do AuthProvider');
    }
    return value;
}
/** Cliente da API com o token de quem entrou. */
export const useApi = () => useAuth().api;
//# sourceMappingURL=AuthProvider.js.map